import { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation, useNavigate } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, useTheme, experimentalStyled as styled } from '@material-ui/core/styles';
import { Box, List, ListItem, Collapse, ListItemText, ListItemIcon, ListSubheader } from '@material-ui/core';

// hooks
import usePermissions from '../hooks/usePermissions';

// ----------------------------------------------------------------------

const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(5),
    color: theme.palette.text.primary
  })
);

const ListItemStyle = styled((props) => <ListItem button disableGutters {...props} />, {
  shouldForwardProp: (prop) => prop !== 'isOpenSidebar'
})(({ theme, isOpenSidebar }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: isOpenSidebar ? theme.spacing(5) : theme.spacing(0),
  paddingRight: isOpenSidebar ? theme.spacing(2.5) : theme.spacing(0),
  justifyContent: isOpenSidebar ? 'flex-start' : 'center',
  color: theme.palette.text.secondary,
  '&:before': {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: 'none',
    position: 'absolute',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main
  }
}));

const ListItemIconStyle = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'isOpenSidebar'
})(({ isOpenSidebar }) => ({
  width: 22,
  height: 22,
  minWidth: 'auto',
  marginRight: isOpenSidebar ? 16 : 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  depth: PropTypes.number,
  isOpenSidebar: PropTypes.bool
};

// Helper: recursively collect all permissionIds from children,
// excluding items flagged with excludeFromModuleCheck
const collectChildPermissionIds = (items) => {
  const ids = [];
  items.forEach((child) => {
    if (child.permissionId && !child.excludeFromModuleCheck) {
      ids.push(child.permissionId);
    }
    if (child.children) {
      ids.push(...collectChildPermissionIds(child.children));
    }
  });
  return [...new Set(ids)];
};

// Helper: recursively find first allowed child path
const getFirstAllowedChildPath = (children, hasPermission) => {
  for (const child of children) {
    if (child.permissionId && !hasPermission(child.permissionId)) {
      continue;
    }
    if (child.path) {
      return child.path;
    }
    if (child.children) {
      const nestedPath = getFirstAllowedChildPath(child.children, hasPermission);
      if (nestedPath) return nestedPath;
    }
  }
  return null;
};

function NavItem({ item, active, depth = 0, isOpenSidebar = true, onOpenSidebar }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children, permissionId } = item;
  const [open, setOpen] = useState(isActiveRoot);

  // Hide the item entirely if user lacks the required permission
  if (permissionId && !hasPermission(permissionId)) {
    return null;
  }

  // Module-level hiding: if this item has children, collect all
  // permission-protected child IDs. If ALL are denied, hide the parent.
  if (children) {
    const childPermIds = collectChildPermissionIds(children);
    if (childPermIds.length > 0 && childPermIds.every((id) => !hasPermission(id))) {
      return null;
    }
  }

  const handleOpen = () => {
    if (!isOpenSidebar && children) {
      if (onOpenSidebar) {
        onOpenSidebar();
      }
      const targetPath = getFirstAllowedChildPath(children, hasPermission);
      if (targetPath) {
        navigate(targetPath);
      }
      setOpen(true);
    } else {
      setOpen((prev) => !prev);
    }
  };

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    '&:before': { display: 'block' }
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium'
  };

  // Increase padding based on depth
  const paddingLeft = 5 + depth * 2;

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          isOpenSidebar={isOpenSidebar}
          sx={{
            pl: isOpenSidebar ? paddingLeft : 0,
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle isOpenSidebar={isOpenSidebar}>{icon && icon}</ListItemIconStyle>
          {isOpenSidebar && <ListItemText disableTypography primary={title} />}
          {isOpenSidebar && info && info}
          {isOpenSidebar && (
            <Box
              component={Icon}
              icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
              sx={{ width: 16, height: 16, ml: 1 }}
            />
          )}
        </ListItemStyle>

        {isOpenSidebar && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((child) => (
                <NavItem 
                  key={child.title} 
                  item={child} 
                  active={active} 
                  depth={depth + 1} 
                  isOpenSidebar={isOpenSidebar}
                />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      isOpenSidebar={isOpenSidebar}
      sx={{
        pl: isOpenSidebar ? paddingLeft : 0,
        ...(isActiveRoot && (depth === 0 ? activeRootStyle : activeSubStyle))
      }}
    >
      <ListItemIconStyle isOpenSidebar={isOpenSidebar}>
        {depth === 0 ? (
          icon && icon
        ) : (
          <Box
            component="span"
            sx={{
              width: 4,
              height: 4,
              display: 'flex',
              borderRadius: '50%',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'text.disabled',
              transition: (theme) => theme.transitions.create('transform'),
              ...(isActiveRoot && {
                transform: 'scale(2)',
                bgcolor: 'primary.main'
              })
            }}
          />
        )}
      </ListItemIconStyle>
      {isOpenSidebar && <ListItemText disableTypography primary={title} />}
      {isOpenSidebar && info && info}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
  isOpenSidebar: PropTypes.bool
};

export default function NavSection({ navConfig, isOpenSidebar = true, onOpenSidebar, ...other }) {
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items } = list;
        return (
          <List key={subheader} disablePadding>
            {isOpenSidebar && <ListSubheaderStyle>{subheader}</ListSubheaderStyle>}
            {items.map((item) => (
              <NavItem 
                key={item.title} 
                item={item} 
                active={match} 
                isOpenSidebar={isOpenSidebar} 
                onOpenSidebar={onOpenSidebar} 
              />
            ))}
          </List>
        );
      })}
    </Box>
  );
}